//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { ConvertHelper } from "../../../../../helpers/convert-helper"

import { Measure } from "../../../../../models/custom/measure"
import { MeasureCollection } from "../../../../../models/custom/measure-collection"

import { TypeHelper } from '../../../../../helpers/type-helper'

import { SeriesInfo, MeasureCollectionChartExtension } from "./measure-collection.chart.extension"
import { IIMRChartExtensionController } from "../../chart.controller"

// Private Types

type PieChartDataPoint = {
    dataType: string,
    value: any,
    tag: {
        seriesInfo: SeriesInfo
    }
}

// Private Types End


export class MeasureCollectionChartExtensionPieChart {

    // Members

    pieChart: DevExpress.viz.dxPieChart
    seriesInfos: SeriesInfo[]
    chartController: IIMRChartExtensionController

    // Members End


    // Ctor

    constructor(private baseExtension: MeasureCollectionChartExtension) { }

    // Ctor End

    public getOptions(): DevExpress.viz.charts.dxPieChartOptions {
        return {
            palette: "Office",

            animation: {
                enabled: false
            },

            commonSeriesSettings: {
                argumentField: 'dataType',
                argumentType: 'string',

                minSegmentSize: 0,
            },

            legend: {
                columnItemSpacing: 2,
                horizontalAlignment: 'center',
                itemTextPosition: 'right',
                orientation: 'horizontal',
                paddingLeftRight: 20,
                rowItemSpacing: 2,

                customizeText: (e: any) => this.customizeLegendText(e)
            },

            resolveLabelOverlapping: 'shift',

            tooltip: {
                enabled: true,

                customizeTooltip: (e: any) => this.customizeTooltip(e)
            },

            onInitialized: (e: any) => this.pieChart = e.component,
            onLegendClick: (e: any) => this.onLegendClick(e)
        }
    }

    public initialize(chartController: IIMRChartExtensionController): JQueryPromise<void> {
        this.chartController = chartController

        return $.Deferred<void>().resolve().promise()
    }

    public refresh(measureCollection: MeasureCollection[], seriesInfo: SeriesInfo[]): JQueryPromise<void> {
        this.seriesInfos = seriesInfo

        const chartType: string = _.size(this.seriesInfos) > 0 ? this.baseExtension.getDevExpressChartType(_.first(this.seriesInfos).type) : 'pie'

        return $.when<any>(this.createSeries(measureCollection))
            .then((series: DevExpress.viz.ChartSeries[]) => {
                const chartDataPoints: PieChartDataPoint[] = this.getPieChartDataPoints(measureCollection)

                if (this.pieChart != null) {
                    this.pieChart.beginUpdate()
                    this.pieChart.option('type', chartType)
                    this.pieChart.option('series', series)
                    this.pieChart.option('dataSource', chartDataPoints)

                    this.pieChart.endUpdate()
                }
            })
    }

    // Series

    private createSeries(measureCollection: MeasureCollection[]): DevExpress.viz.PieChartSeries {
        return {
            argumentField: "dataType",
            valueField: 'value',

            label: {
                visible: true,
                connector: { visible: true },

                font: {
                    color: "#000",
                },

                customizeText: (e: any) => this.customizeLabelText(e)
            },
        }
    }

    // Series End


    // Data

    private getPieChartDataPoints(measureCollections: MeasureCollection[]): PieChartDataPoint[] {
        const dataPoints: PieChartDataPoint[] = []

        _.forEach(this.seriesInfos, (seriesInfo: SeriesInfo) => {
            const measureCollection = _.find(measureCollections, (measureCollection: MeasureCollection) => {
                return measureCollection.idDataType === seriesInfo.dataTypeInGroup.idDataType
            })

            if (measureCollection != null) {
                const sortedMeasures: Measure[] = _.sortBy(measureCollection.measures, (measure: Measure) => measure.time)
                const latestMeasure: Measure = _.first(sortedMeasures)

                dataPoints.push({
                    dataType: seriesInfo.dataTypeInGroup.idDataType.toString(),
                    value: latestMeasure.value,
                    tag: {
                        seriesInfo
                    }
                })
            }
        })

        return dataPoints
    }

    // Data End


    // Chart

    private customizeLabelText(e: any): string {
        if (e.point.tag != null) {
            const seriesInfo: SeriesInfo = e.point.tag.seriesInfo

            if (seriesInfo != null) {
                const wrappedUnitText: string = this.baseExtension.getUnitWrappedText(seriesInfo.dataTypeInGroup)

                if (!TypeHelper.isNullOrEmpty(wrappedUnitText)) {
                    return `${seriesInfo.dataTypeInGroup.toString()} ${wrappedUnitText}`
                }

                return seriesInfo.dataTypeInGroup.toString()
            }
        }
    }

    private customizeLegendText(e: any): string {
        let legendText: string = e.pointName

        if (this.pieChart != null) {
            _.forEach(this.pieChart.getAllSeries(), (series: DevExpress.viz.baseSeriesObject) => {
                const point: any = series.getPointByPos(e.pointIndex)

                if (point != null && point.tag != null) {
                    const seriesInfo: SeriesInfo = point.tag.seriesInfo

                    if (seriesInfo != null) {
                        const wrappedUnitText: string = this.baseExtension.getUnitWrappedText(seriesInfo.dataTypeInGroup)

                        if (!TypeHelper.isNullOrEmpty(wrappedUnitText)) {
                            legendText = `${seriesInfo.dataTypeInGroup.toString()} ${wrappedUnitText}`
                        }
                        else {
                            legendText = seriesInfo.dataTypeInGroup.toString()
                        }
                    }
                }
            })
        }

        return legendText
    }

    private customizeTooltip(e: any): any {
        const seriesInfo: SeriesInfo = (e.point.tag as any).seriesInfo
        const container: JQuery = $('<div/>')

        if (seriesInfo != null) {
            const wrappedUnitText: string = this.baseExtension.getUnitWrappedText(seriesInfo.dataTypeInGroup)
            let tooltipText: string = `${seriesInfo.dataTypeInGroup.toString()}: ${e.value} ${wrappedUnitText}`

            if (!TypeHelper.isNullOrEmpty(wrappedUnitText)) {
                tooltipText += ` (${e.percentText})`
            }

            container.append($('<div/>').text(tooltipText))
        }

        return { html: container.html() }
    }

    private onLegendClick(e: any): void {
        const point: DevExpress.viz.piePointObject = _.first<DevExpress.viz.piePointObject>(e.points)

        if (point != null) {
            const state: any = this.chartController.getExtensionState() || {}

        }
    }

    // Chart End
}